import { useEffect, useRef, type SyntheticEvent, type RefObject, type MutableRefObject } from "react";
import { isPresignedUrlExpired } from "../utils/utils";
import useDisplaySnackbar from "../utils/useDisplaySnackbar";

export type MediaElementRef =
  | RefObject<HTMLMediaElement | null>
  | MutableRefObject<HTMLMediaElement | null>
  | (() => HTMLMediaElement | null);

export interface UseMediaPlaybackRecoveryOptions {
  mediaRef: MediaElementRef;
  currentUrl?: string;
  onUrlUpdate?: (url: string) => void;
  refetchMediaUrl: () => Promise<any>;
  refetchSpritesheetUrl?: () => Promise<any> | void;
  hasSpritesheet?: boolean;
  isActive?: boolean;
  getFallbackResumeTime?: () => number;
  onTimeUpdateCallback?: (currentTime: number) => void;
  onSeekingCallback?: (seekTime: number) => void;
}

export interface UseMediaPlaybackRecoveryReturn {
  refreshMediaUrl: () => Promise<void>;
  resetRecoveryState: () => void;
  mediaHandlers: {
    onError: (e: SyntheticEvent<HTMLMediaElement>) => void;
    onPlaying: () => void;
    onPlay: () => void;
    onPause: (e: SyntheticEvent<HTMLMediaElement>) => void;
    onLoadedMetadata: (e: SyntheticEvent<HTMLMediaElement>) => void;
    onSeeking: (e: SyntheticEvent<HTMLMediaElement>) => void;
    onSeeked: () => void;
    onWaiting: (e: SyntheticEvent<HTMLMediaElement>) => void;
    onStalled: (e: SyntheticEvent<HTMLMediaElement>) => void;
    onTimeUpdate: (e: SyntheticEvent<HTMLMediaElement>) => void;
  };
  refreshingVideoUrlRef: MutableRefObject<boolean>;
  recoveryTargetTimeRef: MutableRefObject<number | null>;
  lastSeekTimeRef: MutableRefObject<number | null>;
  refreshRetryCountRef: MutableRefObject<number>;
  wasPlayingRef: MutableRefObject<boolean>;
}

export function useMediaPlaybackRecovery({
  mediaRef,
  currentUrl,
  onUrlUpdate,
  refetchMediaUrl,
  refetchSpritesheetUrl,
  hasSpritesheet = false,
  isActive = true,
  getFallbackResumeTime,
  onTimeUpdateCallback,
  onSeekingCallback,
}: UseMediaPlaybackRecoveryOptions): UseMediaPlaybackRecoveryReturn {
  const { showSnackbar } = useDisplaySnackbar();

  const refreshingVideoUrlRef = useRef(false);
  const lastRefreshAttemptRef = useRef(0);
  const refreshRetryCountRef = useRef(0);
  const recoveryTargetTimeRef = useRef<number | null>(null);
  const lastSeekTimeRef = useRef<number | null>(null);
  const wasPlayingRef = useRef(false);
  const hasShownErrorSnackbarRef = useRef(false);

  const getMedia = (): HTMLMediaElement | null => {
    if (typeof mediaRef === "function") {
      return mediaRef();
    }
    return mediaRef?.current ?? null;
  };

  const refreshMediaUrl = async () => {
    const media = getMedia();
    if (!media || !isActive) return;

    if (refreshingVideoUrlRef.current) {
      if (lastSeekTimeRef.current !== null) {
        recoveryTargetTimeRef.current = lastSeekTimeRef.current;
      }
      return;
    }

    if (refreshRetryCountRef.current >= 2) {
      console.warn("Max refresh retries reached for media URL");
      if (!hasShownErrorSnackbarRef.current) {
        hasShownErrorSnackbarRef.current = true;
        showSnackbar("error", "Unable to load the video. Please try again later.");
      }
      return;
    }

    const now = Date.now();
    const timeSinceLastAttempt = now - lastRefreshAttemptRef.current;
    if (timeSinceLastAttempt < 5000) {
      return;
    }

    refreshingVideoUrlRef.current = true;
    lastRefreshAttemptRef.current = now;
    refreshRetryCountRef.current += 1;

    const fallbackTime = getFallbackResumeTime ? getFallbackResumeTime() : 0;
    const previousTime =
      lastSeekTimeRef.current !== null
        ? lastSeekTimeRef.current
        : (media.currentTime > 0 ? media.currentTime : fallbackTime);
    lastSeekTimeRef.current = null;
    recoveryTargetTimeRef.current = previousTime;

    try {
      if (hasSpritesheet && refetchSpritesheetUrl) {
        void refetchSpritesheetUrl();
      }

      const refetchResult = await refetchMediaUrl();
      const freshUrl = refetchResult?.data?.data?.downloadUrl;
      const activeSrc = media.src || currentUrl;

      if (
        refetchResult?.isError ||
        !freshUrl ||
        typeof freshUrl !== "string" ||
        freshUrl === activeSrc ||
        isPresignedUrlExpired(freshUrl)
      ) {
        throw new Error("No fresh video URL returned by the API");
      }

      onUrlUpdate?.(freshUrl);
      media.src = freshUrl;
    } catch (error) {
      console.error("Failed to refresh video URL:", error);
      refreshingVideoUrlRef.current = false;
      recoveryTargetTimeRef.current = null;
      if (refreshRetryCountRef.current >= 2) {
        showSnackbar("error", "Video playback failed. Please reload and try again.");
      }
    }
  };

  const handleWaitingOrStalled = (media: HTMLMediaElement) => {
    if (refreshingVideoUrlRef.current) return;

    const activeSrc = media.src || currentUrl;
    if (activeSrc && isPresignedUrlExpired(activeSrc)) {
      void refreshMediaUrl();
    }
  };

  useEffect(() => {
    const handleVisibilityChange = async () => {
      const media = getMedia();
      if (!media || !isActive) return;

      if (document.hidden) {
        wasPlayingRef.current = !media.paused;
        return;
      }

      const activeSrc = media.src || currentUrl;
      if (!activeSrc) return;

      if (media.networkState === HTMLMediaElement.NETWORK_NO_SOURCE) {
        if (isPresignedUrlExpired(activeSrc)) {
          void refreshMediaUrl();
        } else {
          const fallbackTime = getFallbackResumeTime ? getFallbackResumeTime() : 0;
          const previousTime =
            lastSeekTimeRef.current !== null
              ? lastSeekTimeRef.current
              : (media.currentTime > 0 ? media.currentTime : fallbackTime);
          recoveryTargetTimeRef.current = previousTime;
          refreshingVideoUrlRef.current = true;
          media.src = activeSrc;
          media.load();
        }
        return;
      }

      if (isPresignedUrlExpired(activeSrc)) {
        void refreshMediaUrl();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("focus", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("focus", handleVisibilityChange);
    };
  }, [isActive, currentUrl]);

  const resetRecoveryState = () => {
    refreshingVideoUrlRef.current = false;
    refreshRetryCountRef.current = 0;
    recoveryTargetTimeRef.current = null;
    lastSeekTimeRef.current = null;
    wasPlayingRef.current = false;
    lastRefreshAttemptRef.current = 0;
    hasShownErrorSnackbarRef.current = false;
  };

  const mediaHandlers = {
        onError: (e: SyntheticEvent<HTMLMediaElement>) => {
      const media = e.currentTarget;
      const activeSrc = media.src || currentUrl;
      const isNetworkOrSrcError =
        media.error?.code === MediaError.MEDIA_ERR_NETWORK ||
        media.error?.code === MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED;

      if (activeSrc && (isPresignedUrlExpired(activeSrc) || isNetworkOrSrcError)) {
        void refreshMediaUrl();
      } else {
        console.error("Media playback error:", media.error?.code);
      }
    },
    onPlaying: () => {
      wasPlayingRef.current = true;
      refreshRetryCountRef.current = 0;
    },
    onPlay: () => {
      wasPlayingRef.current = true;
    },
    onPause: (e: SyntheticEvent<HTMLMediaElement>) => {
      const media = e.currentTarget;
      if (document.hidden) return;
      if (!media.error && !refreshingVideoUrlRef.current) {
        wasPlayingRef.current = false;
      }
    },
    onLoadedMetadata: (e: SyntheticEvent<HTMLMediaElement>) => {
      const media = e.currentTarget;
      refreshingVideoUrlRef.current = false;
      const fallbackTime = getFallbackResumeTime ? getFallbackResumeTime() : 0;
      const targetTime =
        recoveryTargetTimeRef.current !== null
          ? recoveryTargetTimeRef.current
          : fallbackTime;
      recoveryTargetTimeRef.current = null;
      if (targetTime > 0) {
        media.currentTime = Math.min(targetTime, media.duration || targetTime);
      }
      if (wasPlayingRef.current) {
        media.play().catch(() => { });
      }
    },
    onSeeking: (e: SyntheticEvent<HTMLMediaElement>) => {
      const seekTime = e.currentTarget.currentTime;
      lastSeekTimeRef.current = seekTime;
      recoveryTargetTimeRef.current = seekTime;
      onSeekingCallback?.(seekTime);
      const activeSrc = e.currentTarget.src || currentUrl;
      if (activeSrc && isPresignedUrlExpired(activeSrc)) {
        void refreshMediaUrl();
      }
    },
    onSeeked: () => {
      if (!refreshingVideoUrlRef.current) {
        lastSeekTimeRef.current = null;
      }
    },
    onWaiting: (e: SyntheticEvent<HTMLMediaElement>) => {
      handleWaitingOrStalled(e.currentTarget);
    },
    onStalled: (e: SyntheticEvent<HTMLMediaElement>) => {
      handleWaitingOrStalled(e.currentTarget);
    },
    onTimeUpdate: (e: SyntheticEvent<HTMLMediaElement>) => {
      onTimeUpdateCallback?.(e.currentTarget.currentTime);
    },
  };

  return {
    refreshMediaUrl,
    resetRecoveryState,
    mediaHandlers,
    refreshingVideoUrlRef,
    recoveryTargetTimeRef,
    lastSeekTimeRef,
    refreshRetryCountRef,
    wasPlayingRef,
  };
}
